import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { showBanner } from '../../global-state/banner-slice'
import { InvestmentLimitRequestListItem } from '../../lib/types'
import { investmentLimitRequestServices } from '../../services'
import { InvestmentLimitRequestsListPresenter } from './investment-limit-requests-list-presenter'

const PAGE_SIZE = 20
const DEFAULT_STATUS = 'SUBMITTED'
const ALL_STATUS = 'ALL'

const statusForApi = (statusParam: string | null): string | undefined => {
  if (statusParam === null) return DEFAULT_STATUS
  if (statusParam === ALL_STATUS || statusParam === '') return undefined
  return statusParam
}

const statusForUi = (statusParam: string | null): string => {
  if (statusParam === null) return DEFAULT_STATUS
  if (statusParam === ALL_STATUS) return ALL_STATUS
  return statusParam
}

export function InvestmentLimitRequestsListContainer() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchParams, setSearchParams] = useSearchParams()

  const statusParam = searchParams.get('status')
  const status = statusForUi(statusParam)
  const apiStatus = statusForApi(statusParam)
  const investorIdFilter = searchParams.get('investorId') || undefined

  const [requests, setRequests] = useState<InvestmentLimitRequestListItem[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)

  const load = useCallback(async () => {
    setIsLoading(true)
    const offset = (currentPage - 1) * PAGE_SIZE
    const data = await investmentLimitRequestServices.list({
      status: apiStatus,
      investorId: investorIdFilter,
      limit: PAGE_SIZE,
      offset,
    })
    if (data.status === 200) {
      setRequests(data.data)
      setTotal(data.total)
    } else {
      setRequests([])
      setTotal(0)
      dispatch(
        showBanner({
          text: data.message || 'Failed to load investment limit requests',
          variant: 'error-banner',
        }),
      )
    }
    setIsLoading(false)
  }, [apiStatus, currentPage, dispatch, investorIdFilter])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    setCurrentPage(1)
  }, [apiStatus, investorIdFilter])

  const pagination = useMemo(() => {
    const offset = (currentPage - 1) * PAGE_SIZE
    return {
      total,
      limit: PAGE_SIZE,
      count: Math.min(PAGE_SIZE, Math.max(0, total - offset)),
      offset,
    }
  }, [currentPage, total])

  const handleStatusChange = (value: string) => {
    const next = new URLSearchParams(searchParams)
    if (!value || value === ALL_STATUS) {
      next.set('status', ALL_STATUS)
    } else {
      next.set('status', value)
    }
    setSearchParams(next, { replace: true })
  }

  const resetFilters = () => {
    const next = new URLSearchParams()
    next.set('status', DEFAULT_STATUS)
    setSearchParams(next, { replace: true })
    setCurrentPage(1)
  }

  const handleView = (row: InvestmentLimitRequestListItem) => {
    navigate(`/investment-limit-requests/${row.id}`, { state: { listItem: row } })
  }

  return (
    <InvestmentLimitRequestsListPresenter
      requests={requests}
      isLoading={isLoading}
      status={status}
      investorIdFilter={investorIdFilter}
      handleStatusChange={handleStatusChange}
      resetFilters={resetFilters}
      pagination={pagination}
      currentPage={currentPage}
      handleCurrentPage={setCurrentPage}
      handleView={handleView}
    />
  )
}
